/* eslint-disable react-hooks/exhaustive-deps */
import { MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { Menu, Affix, Drawer, Button, Grid } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  ADMIN_BATCHES,
  ADMIN_USERS,
  ADMIN_COURSES,
  HOME,
  ADMIN_USER_QUERIES,
  ADMIN_DASHBOARD,
  ADMIN_ADMIN_MANAGEMENT,
  DISCORD_ANALYTICS_QUERY_ANALYTICS,
  CP_SHEET_PROBLEMS,
  DISCORD_ANALYTICS_EXPENSE_ANALYSIS,
  DISCORD_ANALYTICS_MENTOR_PERFORMANCE,
  DISCORD_ANALYTICS_MENTOR_DATA,
  ADMIN_EDUCATOR_MANAGEMENT,
  ADMIN_REFERRAL_DASHBOARD,
  SURVEY_SYSTEM_DASHBOARD,
  BULK_NOTIFICATIONS_CAMPAIGNS,
  BULK_NOTIFICATIONS_MAILING_LISTS,
  BULK_NOTIFICATIONS_MAILING_ASSETS,
  DISCORD_ANALYTICS_QUERIES_DASHBOARD,
  CP_SHEET_ANALYTICS_GENERAL,
  CP_SHEET_ANALYTICS_PROBLEM,
  CP_SHEET_ANALYTICS_USER,
  CP_SHEET_COMMENT_MODERATION,
  ADMIN_CONFIG_MANAGEMENT,
  PAYMENTS_DISCOUNT_MANAGEMENT,
  ADMIN_SALES,
  GLOBAL_CONTESTS_MANAGEMENT,
  PCD_SNIPPETS_MANAGEMENT,
  CAREERS_MANAGEMENT,
  COURSE_SESSIONS_MANAGEMENT,
  ADMIN_GUIDANCE_SESSIONS,
  COURSE_ANALYTICS,
  CP_SHEET_LEADERBOARD,
  backendUrlByEnv,
  WEBSITE_BLOGS_MANAGEMENT,
  WEBSITE_BLOGS_STATISTICS,
  PAYMENTS_LEADS,
  WEBSITE_ANNOUNCEMENTS_MANAGEMENT,
  COURSE_MODULES_MANAGEMENT,
  COURSE_MODULES_LINKING,
  COURSE_CONTEST_MANAGEMENT,
  WEBSITE_MENTORS_MANAGEMENT,
  WEBSITE_TESTIMONIALS_MANAGEMENT,
  WAITLIST_MANAGEMENT,
  WEBSITE_FEATURED_PROFILES_MANAGEMENT,
  EXTERNAL_PROBLEMS_MANAGEMENT,
  TAG_MAPPINGS,
  REWARD_KIT_CLAIMS
} from "../../constants/routes";
import * as actionTypes from "../../redux/actions";
import ThemeToggle from "../ThemeToggle";

// Sticky table headers and pinned columns paint at z-index 2-3, so the affixed navbar has to sit
// above them while staying below antd's overlays (Drawer and Modal both start at 1000).
const NAVBAR_Z_INDEX = 10;

// Matches antd's dark Menu surface so the compact bar and drawer read as one element.
const NAVBAR_DARK_BACKGROUND = "#001529";

const THEME_TOGGLE_MENU_KEY = "theme-toggle";

const Navbar = () => {
  const { config: userConfig, env: currentEnv } =
    useSelector((state) => state?.reducer?.authInfo) || {};
  const dispatch = useDispatch();
  const ROOT_URL = backendUrlByEnv(currentEnv);
  const [items, setItems] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const screens = Grid.useBreakpoint();

  // Below lg the horizontal menu collapses everything into one overflow dropdown, and its nested
  // submenus open past the right edge of the viewport instead of flipping, which scrolls the page.
  const isCompactNav = screens.lg === false;

  const handleLogout = async () => {
    axios
      .post(`${ROOT_URL}/admin/logout`)
      .then(() => {
        dispatch({
          type: actionTypes.SET_AUTH_INFO,
          authInfo: { isLoggedIn: false, config: {}, env: currentEnv }
        });
      })
      .catch((err) => {
        console.error("Logout failed:", err);
      });
  };
  const createNavLinkElement = (link, label) => {
    return (
      <Link to={link} onClick={label === "Logout" ? handleLogout : undefined}>
        <i className="fa fa-building" aria-hidden="true" /> {label}
      </Link>
    );
  };

  const createNavLinkObject = (link, label, key) => {
    return {
      label: createNavLinkElement(link, label),
      key: key
    };
  };

  const getBulkNotificationAllowedLinks = (userConfig) => {
    let allowedLinks = [];
    if (userConfig?.bulkNotifications?.hasCampaignAccess) {
      allowedLinks.push(
        createNavLinkObject(
          BULK_NOTIFICATIONS_MAILING_LISTS,
          "Mailing Lists",
          "bulkNotificationMailingLists"
        )
      );
    }
    if (userConfig?.bulkNotifications?.hasMailingListAccess) {
      allowedLinks.push(
        createNavLinkObject(BULK_NOTIFICATIONS_CAMPAIGNS, "Campaigns", "bulkNotificationCampaigns")
      );
    }
    if (userConfig?.bulkNotifications?.hasMailingAssetsAccess) {
      allowedLinks.push(
        createNavLinkObject(
          BULK_NOTIFICATIONS_MAILING_ASSETS,
          "Mailing Assets",
          "bulkNotificationMailingAssets"
        )
      );
    }
    return allowedLinks;
  };

  useEffect(() => {
    let navItems = [];
    if (userConfig.dashboard.hasAccess) {
      navItems.push(createNavLinkObject(ADMIN_DASHBOARD, "Dashboard", "adminDashboard"));
    }
    if (userConfig.users.hasAccess) {
      navItems.push(createNavLinkObject(ADMIN_USERS, "Users", "adminUsers"));
    }
    if (userConfig?.rewardKitClaims?.hasAccess) {
      navItems.push(createNavLinkObject(REWARD_KIT_CLAIMS, "Reward Kit Claims", "adminRewardKitClaims"));
    }
    if (userConfig.courses.hasAccess) {
      navItems.push(createNavLinkObject(ADMIN_COURSES, "Courses", "adminCourses"));
    }
    if (userConfig?.courseContentManagement?.hasAccess) {
      const navItem = {
        label: "Course Content Mgmt",
        key: "coursesContentManagement",
        children: []
      };
      if (userConfig?.courseContentManagement?.hasModuleManagementAccess) {
        navItem.children.push(
          createNavLinkObject(
            COURSE_MODULES_MANAGEMENT,
            "Manage Modules",
            "adminCourseModulesManagement"
          )
        );
      }

      if (userConfig?.courseContentManagement?.hasModuleLinkingAccess) {
        navItem.children.push(
          createNavLinkObject(
            COURSE_MODULES_LINKING,
            "Manage Module Linking",
            "adminCourseModulesLinking"
          )
        );
      }

      if (userConfig?.courseContentManagement?.hasContestManagementAccess) {
        navItem.children.push(
          createNavLinkObject(
            COURSE_CONTEST_MANAGEMENT,
            "Manage Contests",
            "adminCourseContestManagement"
          )
        );
      }

      navItems.push(navItem);
    }
    if (userConfig?.courseAnalytics?.hasAccess) {
      navItems.push(
        createNavLinkObject(COURSE_ANALYTICS, "Course Analytics", "adminCourseAnalytics")
      );
    }
    if (userConfig.batches.hasAccess) {
      navItems.push(createNavLinkObject(ADMIN_BATCHES, "Batches", "adminBatches"));
    }
    if (userConfig.admins.hasAccess) {
      navItems.push(createNavLinkObject(ADMIN_ADMIN_MANAGEMENT, "Admins", "adminAdminManagement"));
    }
    if (userConfig.appConfig.hasAccess) {
      navItems.push(
        createNavLinkObject(
          ADMIN_CONFIG_MANAGEMENT,
          "App Config",
          "adminApplicationConfigManagement"
        )
      );
    }
    if (userConfig.queries.hasAccess) {
      navItems.push(createNavLinkObject(ADMIN_USER_QUERIES, "User Queries", "adminQueries"));
    }
    if (userConfig.discordAnalytics.hasAccess) {
      let navItem = {
        label: "Discord Analytics",
        key: "discordAnalytics",
        children: []
      };
      if (userConfig.discordAnalytics.hasQueriesDataAccess) {
        navItem.children.push(
          createNavLinkObject(
            DISCORD_ANALYTICS_QUERY_ANALYTICS,
            "Query Analytics",
            "adminDiscordAnalyticsQueryAnalytics"
          )
        );
      }
      if (userConfig.discordAnalytics.hasQueriesDashboardAccess) {
        navItem.children.push(
          createNavLinkObject(
            DISCORD_ANALYTICS_QUERIES_DASHBOARD,
            "Queries Dashboard",
            "adminDiscordAnalyticsQueryDashboard"
          )
        );
      }
      if (userConfig.discordAnalytics.hasMentorExpenseAnalysisAccess) {
        navItem.children.push(
          createNavLinkObject(
            DISCORD_ANALYTICS_EXPENSE_ANALYSIS,
            "Expense Analysis",
            "adminDiscordAnalyticsMentorsExpenseAnalysis"
          )
        );
      }
      if (userConfig.discordAnalytics.hasMentorPerformanceAccess) {
        navItem.children.push(
          createNavLinkObject(
            DISCORD_ANALYTICS_MENTOR_PERFORMANCE,
            "Mentors - Performance",
            "adminDiscordAnalyticsMentorsPerformance"
          )
        );
      }
      if (userConfig.discordAnalytics.hasMentorsListAccess) {
        navItem.children.push(
          createNavLinkObject(
            DISCORD_ANALYTICS_MENTOR_DATA,
            "Mentors - Data",
            "adminDiscordAnalyticsMentorsList"
          )
        );
      }
      navItems.push(navItem);
    }
    if (userConfig.cpSheetManagement.hasAccess) {
      let navItem = {
        label: "CP Sheet",
        key: "cpSheet",
        children: [createNavLinkObject(CP_SHEET_PROBLEMS, "Management", "adminCPSheetProblems")]
      };
      if (userConfig?.cpSheetAnalytics?.hasGeneralDashboardAccess) {
        navItem.children.push(
          createNavLinkObject(
            CP_SHEET_ANALYTICS_GENERAL,
            "CP Sheet General Analytics",
            "adminCpSheetGeneralAnalytics"
          )
        );
      }
      if (userConfig?.cpSheetAnalytics?.hasUserDashboardAccess) {
        navItem.children.push(
          createNavLinkObject(
            CP_SHEET_ANALYTICS_USER,
            "CP Sheet User Analytics",
            "adminCpSheetUserAnalytics"
          )
        );
      }
      if (userConfig?.cpSheetAnalytics?.hasProblemDashboardAccess) {
        navItem.children.push(
          createNavLinkObject(
            CP_SHEET_ANALYTICS_PROBLEM,
            "CP Sheet Problem Analytics",
            "adminCpSheetProblemAnalytics"
          )
        );
      }
      if (userConfig?.cpSheetAnalytics?.hasCommentModerationAccess) {
        navItem.children.push(
          createNavLinkObject(
            CP_SHEET_COMMENT_MODERATION,
            "CP Sheet Comment Moderation",
            "adminCpSheetCommentModeration"
          )
        );
      }
      if (userConfig?.cpSheetManagement?.hasLeaderboardManagementAccess) {
        navItem.children.push(
          createNavLinkObject(CP_SHEET_LEADERBOARD, "Leaderboard", "adminCpSheetLeaderboard")
        );
      }
      navItems.push(navItem);
    }

    if (userConfig?.websiteBlogs?.hasAccess) {
      let navItem = {
        label: "Blogs",
        key: "blogs",
        children: []
      };
      if (userConfig?.websiteBlogs?.hasManagementAccess) {
        navItem.children.push(
          createNavLinkObject(WEBSITE_BLOGS_MANAGEMENT, "Management", "adminWebsiteBlogsManagement")
        );
      }
      if (userConfig?.websiteBlogs?.hasStatisticsAccess) {
        navItem.children.push(
          createNavLinkObject(WEBSITE_BLOGS_STATISTICS, "Statistics", "adminWebsiteBlogsStatistics")
        );
      }
      navItems.push(navItem);
    }

    if (userConfig?.courseEducator?.hasAccess) {
      navItems.push(
        createNavLinkObject(ADMIN_EDUCATOR_MANAGEMENT, "Educators", "adminEducatorManagement")
      );
    }

    if (userConfig?.referralDashboard?.hasAccess) {
      navItems.push(
        createNavLinkObject(ADMIN_REFERRAL_DASHBOARD, "Referral Stats", "adminReferralManagement")
      );
    }

    if (userConfig?.surveySystemDashboard?.hasAccess) {
      navItems.push(
        createNavLinkObject(SURVEY_SYSTEM_DASHBOARD, "Survey Dashboard", "adminSurveyDashboard")
      );
    }

    if (
      userConfig?.bulkNotifications?.hasCampaignAccess ||
      userConfig?.bulkNotifications?.hasMailingListAccess
    ) {
      navItems.push({
        label: "Bulk Notifications",
        key: "bulkNotificationsManagement",
        children: getBulkNotificationAllowedLinks(userConfig)
      });
    }

    if (userConfig?.waitlist?.hasAccess) {
      navItems.push({
        label: "Waitlist",
        key: "waitlistManagement",
        children: [
          createNavLinkObject(WAITLIST_MANAGEMENT, "Manage Waitlists", "waitlistManagementSub")
        ]
      });
    }

    if (userConfig?.payments?.hasAccess) {
      let navItem = {
        label: "Payments",
        key: "payments",
        children: []
      };
      if (userConfig?.payments?.hasDiscountsViewAccess) {
        navItem.children.push(
          createNavLinkObject(
            PAYMENTS_DISCOUNT_MANAGEMENT,
            "Discounts",
            "adminPaymentsDiscountManagement"
          )
        );
      }
      if (userConfig?.payments?.hasSalesAnalyticsAccess) {
        navItem.children.push(
          createNavLinkObject(ADMIN_SALES, "Sales", "adminPaymentsSalesAnalytics")
        );
      }
      if (userConfig?.payments?.hasLeadsViewAccess) {
        navItem.children.push(
          createNavLinkObject(PAYMENTS_LEADS, "Leads", "adminPaymentsLeadsDashboard")
        );
      }
      navItems.push(navItem);
    }

    if (
      userConfig?.globalContests?.hasAccess ||
      userConfig?.globalContests?.hasPcdSnippetsViewAccess
    ) {
      const children = [];
      if (userConfig?.globalContests?.hasAccess) {
        children.push(
          createNavLinkObject(
            GLOBAL_CONTESTS_MANAGEMENT,
            "Management",
            "globalContestsManagementSub"
          )
        );
      }
      if (userConfig?.globalContests?.hasPcdSnippetsViewAccess) {
        children.push(
          createNavLinkObject(PCD_SNIPPETS_MANAGEMENT, "PCD Snippets", "pcdSnippetsManagementSub")
        );
      }
      navItems.push({
        label: "Global Contests",
        key: "globalContestsManagement",
        children: children
      });
    }

    if (userConfig?.websiteAnnouncements?.hasAccess) {
      navItems.push(
        createNavLinkObject(
          WEBSITE_ANNOUNCEMENTS_MANAGEMENT,
          "Announcements",
          "websiteAnnouncementsManagement"
        )
      );
    }

    if (userConfig?.careers?.hasAccess) {
      navItems.push(createNavLinkObject(CAREERS_MANAGEMENT, "Careers", "careersManagement"));
    }

    if (userConfig?.courseSessions?.hasAccess || userConfig?.liveSessions?.hasAccess) {
      navItems.push(
        createNavLinkObject(
          COURSE_SESSIONS_MANAGEMENT,
          "Course Sessions",
          "courseSessionsManagement"
        )
      );
    }

    if (userConfig?.guidanceSessions?.hasAccess) {
      navItems.push(
        createNavLinkObject(ADMIN_GUIDANCE_SESSIONS, "Guidance Sessions", "guidanceSessions")
      );
    }

    if (userConfig?.websiteMentors?.hasAccess) {
      navItems.push(
        createNavLinkObject(WEBSITE_MENTORS_MANAGEMENT, "Mentors", "websiteMentorsManagement")
      );
    }

    if (userConfig?.websiteTestimonials?.hasAccess) {
      navItems.push(
        createNavLinkObject(
          WEBSITE_TESTIMONIALS_MANAGEMENT,
          "Testimonials",
          "websiteTestimonialsManagement"
        )
      );
    }

    if (userConfig?.websiteFeaturedProfiles?.hasAccess) {
      navItems.push(
        createNavLinkObject(
          WEBSITE_FEATURED_PROFILES_MANAGEMENT,
          "Website Profiles",
          "websiteFeaturedProfilesManagement"
        )
      );
    }

    if (userConfig?.externalProblems?.hasAccess) {
      navItems.push(
        createNavLinkObject(
          EXTERNAL_PROBLEMS_MANAGEMENT,
          "External Problems",
          "externalProblemsManagement"
        )
      );
      navItems.push(createNavLinkObject(TAG_MAPPINGS, "Tag Mappings", "tagMappings"));
    }

    navItems.push({
      key: THEME_TOGGLE_MENU_KEY,
      label: <ThemeToggle />
    });
    navItems.push(createNavLinkObject(HOME, "Logout", "adminLogout"));
    // Add the env info
    navItems.push({
      key: "env-info",
      label: `Environment: ${currentEnv}`, // Display the current environment
      disabled: true // Makes it non-clickable
    });
    setItems(navItems);
  }, []);

  if (isCompactNav) {
    return (
      <>
        <Affix offsetTop={0} style={{ zIndex: NAVBAR_Z_INDEX }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 12px",
              background: NAVBAR_DARK_BACKGROUND
            }}
          >
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setIsDrawerOpen(true)}
              aria-label="Open navigation menu"
              style={{ color: "#FFFFFF" }}
            />
            <span style={{ color: "#FFFFFF", fontWeight: 500 }}>TLE Admin</span>
          </div>
        </Affix>
        <Drawer
          placement="left"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          width="min(300px, 85vw)"
          closable
          closeIcon={<CloseOutlined style={{ color: "#FFFFFF" }} />}
          styles={{
            body: { padding: 0, background: NAVBAR_DARK_BACKGROUND },
            header: { background: NAVBAR_DARK_BACKGROUND, borderBottom: "none" }
          }}
        >
          <Menu
            theme="dark"
            mode="inline"
            items={items}
            onClick={({ key }) => {
              // The theme switch lives in the same item list, and toggling it should not navigate.
              if (key !== THEME_TOGGLE_MENU_KEY) {
                setIsDrawerOpen(false);
              }
            }}
            style={{ borderInlineEnd: "none" }}
          />
        </Drawer>
      </>
    );
  }

  return (
    <Affix offsetTop={0} style={{ zIndex: NAVBAR_Z_INDEX }}>
      <Menu theme="dark" overflowedIndicator={<MenuOutlined />} mode="horizontal" items={items} />
    </Affix>
  );
};

export default Navbar;
